import {
	BaseRequestConfig,
	SearchPayload,
	SearchResponse,
	AddSerialsResponse,
	SubmitContainmentPayload,
	AddContainmentResponse,
	MccApi,
} from "lib/api";
import { Sector, Containment, ContainmentModel } from "models";
import { DashboardFilters } from "pages";
import { ContainmentDTO, ContainmentStatusDTO } from "models/dto";
import * as containmentMapper from "models/mapper/containment";
import * as constants from "lib/api/constants";
import { ApiPromiseClient } from "../apiClient/ApiClient";
import { BaseEntity } from "BaseEntity";
import { ContainmentStatusEnum } from "enums/containmentStatus";
import { ContainmentHoldDTO } from "models/dto/containmentHold";
import * as enm from "lib/enum";
import * as containmentStatusMapper from "models/mapper/containmentStatus";
import * as dotEnv from "lib/dotEnv";
import { parse } from "path";

export async function SearchContainments(
	api: ApiPromiseClient,
	filters: SearchPayload<Containment, DashboardFilters>
): Promise<SearchResponse<Containment, DashboardFilters>> {
	const resp = await api.Post<
		SearchResponse<ContainmentDTO, DashboardFilters>
	>(constants.Paths().containmentsSearch, filters);
	const mapped = containmentMapper.New().MapArray(resp.resultList);
	return { ...resp, resultList: mapped };
}

export async function ContainmentById(
	api: ApiPromiseClient,
	id: string
): Promise<Opt<Containment>> {
	const payload = {
		searchFilters: {
			fieldFilters: [
				{
					fieldName: "id" as keyof DashboardFilters,
					filterValue: id,
					filterOperator: "eq",
					filterValueList: [id],
				},
			],
		},
	} as SearchPayload<Containment, DashboardFilters>;
	const response = await SearchContainments(api, payload);
	return response.resultList.First();
}

export async function UnmappedFiles(
	api: ApiPromiseClient,
	containmentId: string
): Promise<string[]> {
	return api.Get((p) => p.unmappedFiles(containmentId));
}

export async function ContainmentDataDownload(
	api: ApiPromiseClient,
	containmentId: string,
	holdSystemId: string,
	plantId: string
): Promise<{ message: string }> {
	return api.Get((p) =>
		p.triggerContainmentDataDownload(containmentId, holdSystemId, plantId)
	);
}

export async function ContainmentTypes(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<BaseEntity[]> {
	return api.Get((p) => p.containmentTypes, config);
}

export async function ContainmentSectors(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<Sector[]> {
	return api.Get((p) => p.containmentSectors, config);
}

export async function SectorsByContainmentType(
	api: ApiPromiseClient,
	typeName: string,
	config?: BaseRequestConfig
): Promise<Sector[]> {
	return api.Get(constants.Paths().sectorsByContainmentType(typeName), config);
}

export async function ContainmentReasons(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<BaseEntity[]> {
	return api.Get(constants.Paths().containmentReasons, config);
}

export async function AddSerials(
	api: ApiPromiseClient,
	body: File,
	id: string,
	byLpn?: boolean,
	config?: BaseRequestConfig
): Promise<AddSerialsResponse> {
	const formData = new FormData();
	formData.append("files", body);
	if (byLpn !== undefined) {
		formData.append("byLpn", String(byLpn));
	}
	const resp = await api.Post<AddSerialsResponse>(
		constants.Paths().addSerials(id),
		formData,
		{
			...config,
			// needs a longer timeout
			signal: MccApi.TimeoutAbortSignal(
				parseInt(dotEnv.Get("API_UPLOAD_TIMEOUT_MS"))
			),
		}
	);

	return resp;
}

export async function UnlockContainment(
	api: ApiPromiseClient,
	body: File,
	id: string,
	byLpn: boolean,
	config?: BaseRequestConfig
): Promise<AddSerialsResponse> {
	const formData = new FormData();
	formData.append("files", body);
	formData.append(
		"request",
		new Blob([JSON.stringify({ byLpn })], { type: "application/json" })
	);
	const resp = await api.Post<AddSerialsResponse>(
		constants.Paths().unlockContainment(id),
		formData,
		{
			...config,
			signal: MccApi.TimeoutAbortSignal(
				parseInt(dotEnv.Get("API_UPLOAD_TIMEOUT_MS"))
			),
		}
	);

	return resp;
}

export async function ReleaseSerials(
	api: ApiPromiseClient,
	body: File,
	id: string,
	plants: any[] = [],
	config?: BaseRequestConfig
): Promise<{ released: number }> {
	const formData = new FormData();
	formData.append("files", body);
	if (plants && plants.length > 0) {
		// Ensure each plant entry matches backend expected shape: [{ plant: { id, name } }]
		const normalized = plants.map((p: any) =>
			p && p.plant ? p : { plant: { id: p.id, name: p.name } }
		);
		formData.append(
			"plants",
			new Blob([JSON.stringify(normalized)], { type: "application/json" })
		);
	}
	return api.Post<{ released: number }>(
		constants.Paths().releaseSerials(id),
		formData,
		config
	);
}

export async function AddContainment(
	api: ApiPromiseClient,
	body: SubmitContainmentPayload,
	config?: BaseRequestConfig
): Promise<AddSerialsResponse> {
	const { file, containment, ...optionalProps } = body;
	const formData = new FormData();
	formData.append("files", file);
	formData.append(
		"containmentModel",
		new Blob(
			[
				JSON.stringify({
					containment,
					...optionalProps,
				}),
			],
			{ type: "application/json" }
		)
	);

	return api.Post<AddContainmentResponse>(
		constants.Paths().addContainment,
		formData,
		{
			...config,
			// needs a longer timeout
			signal: MccApi.TimeoutAbortSignal(
				parseInt(dotEnv.Get("API_UPLOAD_TIMEOUT_MS"))
			),
		}
	);
}

export async function ContainmentStatuses(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<ContainmentStatusEnum> {
	const dtos = await api.Get<ContainmentStatusDTO[]>(
		constants.Paths().containmentStatuses,
		config
	);
	return new enm.Enum(containmentStatusMapper.New().MapArray(dtos));
}

export async function ContainmentInitialHolds(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<ContainmentHoldDTO[]> {
	return api.Get<ContainmentHoldDTO[]>(
		constants.Paths().containmentInitialHold,
		config
	);
}

export async function ContainmentSubsequentHolds(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<ContainmentHoldDTO[]> {
	return api.Get<ContainmentHoldDTO[]>(
		constants.Paths().containmentSubsequentHold,
		config
	);
}

export async function UpdateContainment(
	api: ApiPromiseClient,
	mapper: containmentMapper.ContainmentMapper,
	id: string,
	dto: ContainmentDTO
): Promise<Containment> {
	const payload = mapper.Map(dto).ToApiModel();
	const resp = await api.Put<ContainmentModel>(
		(p) => p.containmentIdPath(id),
		payload
	);
	return mapper.Map(resp.containment);
}

export async function PartSerialStatuses<T>(
	api: ApiPromiseClient,
	config?: BaseRequestConfig
): Promise<T> {
	return api.Get<T>(constants.Paths().partSerialStatuses, config);
}

export type FlexNetPartNumberGatePlant = {
	id?: string;
	name?: string;
	code?: string;
	flexnetVersion?: string | null;
};

/**
 * Server-side FlexNet PN gate: returns plants that do not support Part Number removal
 * (null/blank or FlexNet version &lt; 7.1.0).
 */
export async function EvaluateFlexNetPartNumberGate(
	api: ApiPromiseClient,
	plants: FlexNetPartNumberGatePlant[]
): Promise<FlexNetPartNumberGatePlant[]> {
	return api.Post<FlexNetPartNumberGatePlant[]>(
		constants.Paths().flexNetPartNumberGate,
		{ plants }
	);
}
